import { RefObject, useEffect, useState } from 'react';

import { LatLng, waitForElementToHaveSize } from '@mopla/utils';

type MapController = import('../services/hereapi/MapController').MapController;

interface IUseMapInstanceResult {
	mapController: MapController | null;
	isMapLoading: boolean;
}

export const useMapInstance = (
	ref: RefObject<HTMLDivElement>,
	mapCenter: LatLng,
	zoom = 17
): IUseMapInstanceResult => {
	const [mapController, setMapController] = useState<MapController | null>(
		null
	);
	const [isMapLoading, setIsMapLoading] = useState(true);

	useEffect(() => {
		const refNode = ref.current;
		let instance: MapController | null = null;
		let resizeObserver: ResizeObserver | null = null;
		let cancelSizeWait: (() => void) | null = null;
		let isCancelled = false;

		(async () => {
			const { MapController } = await import(
				'../services/hereapi/MapController'
			);

			if (isCancelled || !refNode) return;

			const { promise: waitForElementToHaveSizePromise, cancel } =
				waitForElementToHaveSize(refNode);
			cancelSizeWait = cancel;

			await waitForElementToHaveSizePromise;

			if (isCancelled) return;

			instance = new MapController(refNode, mapCenter, zoom);
			setMapController(instance);

			resizeObserver = new ResizeObserver(instance.resizeHandler);
			resizeObserver.observe(refNode);

			setIsMapLoading(false);
		})();

		return () => {
			isCancelled = true;
			cancelSizeWait?.();
			resizeObserver?.disconnect();
			instance?.destroy();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return { mapController, isMapLoading };
};
